import React, { useCallback, useEffect, useState, useRef, memo, createElement } from 'react';
import { ExternalLinkIcon, SmartphoneIcon, TabletIcon, MonitorIcon } from 'lucide-react';
interface FormPreviewProps {
  html: string;
  css?: string;
  js?: string;
  submissionScript?: string;
}
export function FormPreview({
  html,
  css,
  js,
  submissionScript
}: FormPreviewProps) {
  const [device, setDevice] = useState<'mobile' | 'tablet' | 'desktop'>('desktop');
  const baseStyles = 'body { font-family: system-ui, -apple-system, sans-serif; padding: 20px; margin: 0; }';
  const styleOpen = '<' + 'style>';
  const styleClose = '</' + 'style>';
  const scriptOpen = '<' + 'script>';
  const scriptClose = '</' + 'script>';
  // Bridge script that runs before all other scripts:
  // 1. Replaces alert() with styled toast notifications
  // 2. Auto-adds "required" attribute to fields whose labels have .req asterisks
  //    so the submission handler's validateForm() catches them
  // 3. Also marks radio groups and checkboxes as required when their label has .req
  // 4. Re-evaluates on form changes and section visibility changes
  const previewBridgeScript = `(function(){
  /* --- Toast notification system (replaces alert) --- */
  function _toast(msg,isErr){
    var t=document.getElementById('_ft52');if(t)t.remove();
    var s=document.createElement('style');
    s.textContent='@keyframes _fti{from{transform:translate(-50%,-16px);opacity:0}to{transform:translate(-50%,0);opacity:1}}@keyframes _fto{from{opacity:1}to{opacity:0;transform:translate(-50%,-16px)}}';
    document.head.appendChild(s);
    t=document.createElement('div');t.id='_ft52';
    t.style.cssText='position:fixed;top:24px;left:50%;transform:translateX(-50%);z-index:2147483647;background:'+(isErr?'#dc2626':'#16a34a')+';color:#fff;padding:14px 18px;border-radius:10px;font-size:14px;font-weight:500;box-shadow:0 8px 24px rgba(0,0,0,0.18);max-width:380px;display:flex;align-items:center;gap:12px;font-family:-apple-system,BlinkMacSystemFont,sans-serif;animation:_fti 0.3s ease';
    var sp=document.createElement('span');sp.textContent=msg;
    var cl=document.createElement('button');cl.innerHTML='\\u00d7';
    cl.style.cssText='background:none;border:none;color:rgba(255,255,255,0.8);font-size:20px;cursor:pointer;padding:0;line-height:1;margin-left:4px;flex-shrink:0';
    function dismiss(){t.style.animation='_fto 0.3s ease forwards';setTimeout(function(){if(t&&t.parentNode)t.remove()},300)}
    cl.onclick=dismiss;t.appendChild(sp);t.appendChild(cl);document.body.appendChild(t);
    setTimeout(dismiss,5000);
  }
  window.alert=function(msg){_toast(msg,true);};
  window._formToast=_toast;

  /* --- Visibility check helper --- */
  function _isNodeVisible(node){
    while(node&&node!==document.body){
      if(node.classList&&node.classList.contains('hidden'))return false;
      if(node.style&&node.style.display==='none')return false;
      try{var cs=window.getComputedStyle(node);if(cs.display==='none'||cs.visibility==='hidden')return false;}catch(e){}
      node=node.parentElement;
    }
    return true;
  }

  /* --- Auto-add/remove required attributes based on .req asterisks in labels --- */
  /* Only marks fields as required when their label is currently VISIBLE.           */
  /* Re-runs on form changes so toggling persona sections updates required state.   */
  function autoMarkRequired(){
    var form=document.querySelector('form');
    if(!form)return;
    var labels=form.querySelectorAll('label.field-label, label[for]');
    for(var i=0;i<labels.length;i++){
      var lbl=labels[i];
      if(!lbl.querySelector('.req'))continue;
      var vis=_isNodeVisible(lbl);
      // Check if label has a for attribute pointing to a field
      var forAttr=lbl.getAttribute('for');
      if(forAttr){
        var field=form.querySelector('#'+forAttr);
        if(field){
          if(vis)field.setAttribute('required','');
          else field.removeAttribute('required');
        }
        continue;
      }
      // Check for radio/checkbox groups: label followed by a radio-stack or radio-inline
      var next=lbl.nextElementSibling;
      if(next){
        var radios=next.querySelectorAll('input[type="radio"],input[type="checkbox"]');
        for(var j=0;j<radios.length;j++){
          if(vis)radios[j].setAttribute('required','');
          else radios[j].removeAttribute('required');
        }
      }
      // Check for consent checkboxes inside the same form-group
      var group=lbl.closest('.form-group');
      if(group){
        var cb=group.querySelector('input[type="checkbox"]');
        if(cb){
          if(vis)cb.setAttribute('required','');
          else cb.removeAttribute('required');
        }
      }
    }
  }
  function initAutoRequired(){
    autoMarkRequired();
    var form=document.querySelector('form');
    if(form){
      // Re-run when any form value changes (e.g. persona radio selection)
      form.addEventListener('change',function(){setTimeout(autoMarkRequired,30);});
    }
    // Also observe class/style changes on sections that toggle visibility
    if(window.MutationObserver){
      var sections=document.querySelectorAll('.persona-section,[data-persona],section');
      if(sections.length>0){
        var obs=new MutationObserver(function(){setTimeout(autoMarkRequired,30);});
        for(var s=0;s<sections.length;s++){
          obs.observe(sections[s],{attributes:true,attributeFilter:['class','style']});
        }
      }
    }
  }
  // Run after DOM is ready
  if(document.readyState==='loading'){
    document.addEventListener('DOMContentLoaded',initAutoRequired);
  }else{
    initAutoRequired();
  }
})();`;
  const buildHtmlDoc = useCallback((): string => {
    const isFullDocument = /<!doctype\s+html|<html[\s>]/i.test(html);
    let htmlDoc: string;
    // Build script blocks: bridge first, then submission handler, then user JS
    const alertBlock = scriptOpen + previewBridgeScript + scriptClose;
    const submissionBlock = submissionScript ? scriptOpen + submissionScript + scriptClose : '';
    const jsBlock = js ? scriptOpen + js + scriptClose : '';
    const allScripts = alertBlock + submissionBlock + jsBlock;
    if (isFullDocument) {
      let cleanedHtml = html.replace(/<link[^>]*rel=["']stylesheet["'][^>]*\/?>/gi, '');
      const cssBlock = styleOpen + baseStyles + '\n' + (css || '') + styleClose;
      if (/<\/head>/i.test(cleanedHtml)) {
        htmlDoc = cleanedHtml.replace(/<\/head>/i, cssBlock + '</head>');
      } else if (/<head[^>]*>/i.test(cleanedHtml)) {
        htmlDoc = cleanedHtml.replace(/<head([^>]*)>/i, '<head$1>' + cssBlock);
      } else {
        htmlDoc = '<!DOCTYPE html><html><head>' + cssBlock + '</head><body>' + cleanedHtml + '</body></html>';
      }
      if (allScripts) {
        if (/<\/body>/i.test(htmlDoc)) {
          htmlDoc = htmlDoc.replace(/<\/body>/i, allScripts + '</body>');
        } else {
          htmlDoc += allScripts;
        }
      }
    } else {
      htmlDoc = '<!DOCTYPE html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1">' + styleOpen + baseStyles + '\n' + (css || '') + styleClose + '</head><body>' + html + allScripts + '</body></html>';
    }
    return htmlDoc;
  }, [html, css, js, submissionScript]);
  /** Build a realistic website page that embeds the form */
  const buildTestPageDoc = useCallback((): string => {
    // Separate inline <script> blocks from the HTML so we can place them correctly
    const scriptBlocks: string[] = [];
    const htmlWithoutScripts = html.replace(/<script\b[^>]*>([\s\S]*?)<\/script>/gi, (_match, content) => {
      scriptBlocks.push(content);
      return '';
    });
    const parts: string[] = [];
    parts.push('<!DOCTYPE html>');
    parts.push('<html lang="en">');
    parts.push('<head>');
    parts.push('<meta charset="utf-8">');
    parts.push('<meta name="viewport" content="width=device-width, initial-scale=1">');
    parts.push('<title>Form Test — Embedded Preview</title>');
    parts.push('<link rel="preconnect" href="https://fonts.googleapis.com">');
    parts.push('<link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap" rel="stylesheet">');
    parts.push(styleOpen);
    parts.push(`*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
body {
  font-family: 'Inter', system-ui, -apple-system, sans-serif;
  background: #f1f5f9;
  color: #1e293b;
  min-height: 100vh;
}
.site-header {
  background: #ffffff;
  border-bottom: 1px solid #e2e8f0;
  padding: 0 2rem;
  height: 56px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  box-shadow: 0 1px 3px rgba(0,0,0,0.04);
}
.site-logo {
  font-weight: 700;
  font-size: 1.125rem;
  color: #0f172a;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}
.site-logo-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #3b82f6;
}
.site-nav {
  display: flex;
  gap: 1.5rem;
  align-items: center;
}
.site-nav a {
  font-size: 0.875rem;
  color: #64748b;
  text-decoration: none;
  font-weight: 500;
  transition: color 0.15s;
}
.site-nav a:hover { color: #0f172a; }
.site-main {
  max-width: 960px;
  margin: 2.5rem auto;
  padding: 0 1.5rem;
  transition: max-width 0.3s ease;
}
.site-card {
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04);
  border: 1px solid #e2e8f0;
  overflow: visible;
  position: relative;
}
.site-card-header {
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid #f1f5f9;
}
.site-card-header h1 {
  font-size: 1.125rem;
  font-weight: 600;
  color: #0f172a;
}
.site-card-header p {
  font-size: 0.8125rem;
  color: #64748b;
  margin-top: 0.25rem;
}
.site-card-body {
  padding: 1.5rem;
  position: relative;
  overflow: visible;
}
.site-footer {
  text-align: center;
  padding: 2rem 1rem;
  font-size: 0.75rem;
  color: #94a3b8;
}
.test-badge {
  position: fixed;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  background: #0f172a;
  color: #f8fafc;
  font-size: 0.6875rem;
  font-weight: 600;
  padding: 0.25rem 1rem;
  border-radius: 0 0 6px 6px;
  z-index: 9999;
  letter-spacing: 0.03em;
  box-shadow: 0 2px 8px rgba(0,0,0,0.15);
}
.device-toolbar {
  position: fixed;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  background: white;
  padding: 8px;
  border-radius: 100px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
  display: flex;
  gap: 8px;
  z-index: 10000;
  border: 1px solid #e2e8f0;
}
.device-btn {
  background: none;
  border: none;
  padding: 8px 16px;
  border-radius: 100px;
  font-size: 14px;
  font-weight: 500;
  color: #64748b;
  cursor: pointer;
  transition: all 0.2s;
}
.device-btn.active {
  background: #f1f5f9;
  color: #0f172a;
}
.device-btn:hover:not(.active) {
  background: #f8fafc;
  color: #334155;
}
/* Device simulation styles */
body.device-mobile .site-main { max-width: 375px; }
body.device-tablet .site-main { max-width: 768px; }
body.device-desktop .site-main { max-width: 960px; }
`);
    // Inject form CSS
    if (css) {
      parts.push('\n/* Form Styles */\n' + css);
    }
    parts.push(styleClose);
    parts.push('</head>');
    parts.push('<body class="device-desktop">');
    parts.push('<div class="test-badge">\u26A1 FORM TEST MODE</div>');
    parts.push(`<header class="site-header">
  <div class="site-logo">
    <span class="site-logo-dot"></span>
    YourSite.com
  </div>
  <nav class="site-nav">
    <a href="#">Home</a>
    <a href="#">About</a>
    <a href="#">Contact</a>
  </nav>
</header>`);
    parts.push(`<main class="site-main">
  <div class="site-card">
    <div class="site-card-header">
      <h1>Sign Up</h1>
      <p>Fill out the form below — this is how it will look when embedded on your website.</p>
    </div>
    <div class="site-card-body">`);
    parts.push(htmlWithoutScripts);
    parts.push(`    </div>
  </div>
</main>`);
    parts.push(`<footer class="site-footer">
  &copy; 2026 YourSite.com — This is a test preview of your embedded form.
</footer>`);
    // Add device toggle + refresh script
    parts.push(`<script>
      function setDevice(device) {
        document.body.className = 'device-' + device;
        document.querySelectorAll('.device-btn').forEach(btn => {
          if (btn.dataset.device === device) {
            btn.classList.add('active');
          } else {
            btn.classList.remove('active');
          }
        });
      }
      function refreshForm() {
        var btn = document.getElementById('refreshBtn');
        if (btn) {
          btn.textContent = 'Refreshing…';
          btn.disabled = true;
        }
        if (window.opener && !window.opener.closed) {
          window.opener.postMessage('refresh-form-test', '*');
          // Fallback timeout in case parent doesn't respond
          setTimeout(function() {
            if (btn) { btn.textContent = '\\u21BB Refresh'; btn.disabled = false; }
          }, 3000);
        } else {
          // No parent window — show message
          if (btn) { btn.textContent = 'Open from editor'; btn.disabled = false; }
          setTimeout(function() {
            if (btn) { btn.textContent = '\\u21BB Refresh'; }
          }, 2000);
        }
      }
    </script>`);
    parts.push(`<div class="device-toolbar">
      <button class="device-btn" data-device="mobile" onclick="setDevice('mobile')">Mobile</button>
      <button class="device-btn" data-device="tablet" onclick="setDevice('tablet')">Tablet</button>
      <button class="device-btn active" data-device="desktop" onclick="setDevice('desktop')">Desktop</button>
      <span style="width:1px;height:24px;background:#e2e8f0;margin:0 4px"></span>
      <button id="refreshBtn" class="device-btn" onclick="refreshForm()" title="Reload with latest saved form changes" style="color:#3b82f6;font-weight:600">&#x21BB; Refresh</button>
    </div>`);
    // Inject bridge script FIRST so alert() becomes toast and required attrs are set before any other script runs
    parts.push(scriptOpen + previewBridgeScript + scriptClose);
    // Inject the submission handler script (before user scripts)
    if (submissionScript) {
      parts.push(scriptOpen + submissionScript + scriptClose);
    }
    // Re-inject all script blocks at the end of body so DOM is ready
    for (const script of scriptBlocks) {
      parts.push(scriptOpen + script + scriptClose);
    }
    // Also inject any separate JS (from the JS editor tab)
    if (js) {
      parts.push(scriptOpen + js + scriptClose);
    }
    parts.push('</body>');
    parts.push('</html>');
    return parts.join('\n');
  }, [html, css, js, submissionScript]);
  // Keep a ref to the test window so we can reuse / refresh it
  const testWindowRef = useRef<Window | null>(null);
  const prevBlobUrlRef = useRef<string | null>(null);
  const handleTestInNewWindow = useCallback(() => {
    const htmlDoc = buildTestPageDoc();
    // Revoke previous blob URL to free memory
    if (prevBlobUrlRef.current) {
      URL.revokeObjectURL(prevBlobUrlRef.current);
      prevBlobUrlRef.current = null;
    }
    // Check if the test window is still open
    const existingWin = testWindowRef.current;
    if (existingWin && !existingWin.closed) {
      // Reuse the existing window — write updated content directly
      try {
        existingWin.document.open();
        existingWin.document.write(htmlDoc);
        existingWin.document.close();
        existingWin.focus();
        return;
      } catch (_e) {
        // Cross-origin or closed — fall through to open new window
      }
    }
    // Open a new window with a named target so browser reuses it
    const blob = new Blob([htmlDoc], {
      type: 'text/html'
    });
    const url = URL.createObjectURL(blob);
    prevBlobUrlRef.current = url;
    const win = window.open(url, 'form-test-window');
    if (win) {
      testWindowRef.current = win;
    }
  }, [buildTestPageDoc]);
  // Listen for refresh requests from the test window
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data === 'refresh-form-test') {
        // Push latest content to the test window
        const win = testWindowRef.current;
        if (win && !win.closed) {
          try {
            const freshDoc = buildTestPageDoc();
            win.document.open();
            win.document.write(freshDoc);
            win.document.close();
          } catch (_e) {
            // If write fails, fall back to opening new window
            handleTestInNewWindow();
          }
        }
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [buildTestPageDoc, handleTestInNewWindow]);
  const htmlDoc = buildHtmlDoc();
  const getDeviceWidth = () => {
    switch (device) {
      case 'mobile':
        return '375px';
      case 'tablet':
        return '768px';
      case 'desktop':
        return '100%';
    }
  };
  return <div className="space-y-3" data-id="element-1777" data-mp="35a0ptc3edcbj">
      <div className="flex items-center justify-between bg-white p-2 rounded-lg border border-gray-200" data-id="element-1778" data-mp="3k5gojuex9we9">
        <div className="flex items-center gap-1 bg-gray-100 p-1 rounded-md" data-id="element-1779" data-mp="3f5kjdruhnyx4">
          <button onClick={() => setDevice('mobile')} className={`p-1.5 rounded transition-colors ${device === 'mobile' ? 'bg-white shadow-sm text-blue-600' : 'text-gray-500 hover:text-gray-700'}`} title="Mobile view (375px)" data-id="element-1780" data-mp="2rfzzn6qigxms">
            <SmartphoneIcon className="h-4 w-4" data-id="element-1781" data-mp="3185nq5dn6uwp" />
          </button>
          <button onClick={() => setDevice('tablet')} className={`p-1.5 rounded transition-colors ${device === 'tablet' ? 'bg-white shadow-sm text-blue-600' : 'text-gray-500 hover:text-gray-700'}`} title="Tablet view (768px)" data-id="element-1782" data-mp="2wekrahrp68zm">
            <TabletIcon className="h-4 w-4" data-id="element-1783" data-mp="1i85jdhqyh9fy" />
          </button>
          <button onClick={() => setDevice('desktop')} className={`p-1.5 rounded transition-colors ${device === 'desktop' ? 'bg-white shadow-sm text-blue-600' : 'text-gray-500 hover:text-gray-700'}`} title="Desktop view (100%)" data-id="element-1784" data-mp="1i9owx0l896e1">
            <MonitorIcon className="h-4 w-4" data-id="element-1785" data-mp="1w8gbsp7wyw38" />
          </button>
        </div>
        <button onClick={handleTestInNewWindow} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-gray-800 hover:bg-gray-700 rounded-md transition-colors shadow-sm" title="Opens a realistic website page with your form embedded — test full interactivity" data-id="element-1786" data-mp="215f4r4fucwzu">
          <ExternalLinkIcon className="h-3.5 w-3.5" data-id="element-1787" data-mp="2fwxg2oxra3cv" />
          Test on Simulated Website
        </button>
      </div>
      <div className="w-full bg-gray-100 rounded border border-gray-200 overflow-hidden flex justify-center py-4" style={{
      height: '60vh',
      minHeight: '500px'
    }} data-id="element-1788" data-mp="2ovsom7o9vpjc">
        <div className="bg-white shadow-sm transition-all duration-300 ease-in-out" style={{
        width: getDeviceWidth(),
        height: '100%'
      }} data-id="element-1789" data-mp="88unngq3pdi3">
          <iframe srcDoc={htmlDoc} title="Form Preview" className="w-full border-0" style={{
          height: '100%'
        }} sandbox="allow-scripts allow-forms allow-modals allow-same-origin" data-id="element-1790" data-mp="3velq04uw32l6" />
        </div>
      </div>
    </div>;
}